// Thin client for the donation backend (server/index.js).
// In dev, requests go through CRA's "proxy" to http://localhost:3001.
// In production, set REACT_APP_API_BASE to the deployed backend origin (or
// leave it empty when Nginx proxies /api on the same origin as the build).
const API_BASE = process.env.REACT_APP_API_BASE || '';

// Fire-and-forget logging of hosted-iframe donation lifecycle events to our own
// backend (server/index.js /api/log-donation). Real charges happen inside the
// IsraelGives iframe and never hit our server, so without this we have no
// in-house record of attempts vs. completions. Never blocks the donation flow.
export function logDonationEvent(payload) {
  try {
    fetch(`${API_BASE}/api/log-donation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // survive the swap to the success screen / any navigation
    }).catch(() => {});
  } catch {
    /* logging must never break checkout */
  }
}

// Attach the donor's optional donor-wall message to a donation that has ALREADY
// been charged and recorded. Collected in the thank-you dialog rather than the
// donor form, so it necessarily arrives after the Gramsci write — the backend
// resolves `token` to that donation and forwards the message on.
//
// Unlike logDonationEvent this is awaited, because the dialog reflects the
// outcome back to the donor. Resolves false on any failure; the caller treats
// that as "leave the field up so they can retry" and never as a donation error.
export async function submitDonationMessage({ token, campaignName, message }) {
  try {
    const res = await fetch(`${API_BASE}/api/donation-message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, campaignName, message }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Fetch the live project record for a landing-page slug — a donation campaign
// or a petition; the record's own project_type says which (projectType.js). The
// backend resolves the slug to a Gramsci campaign id and proxies the
// donationCampaigns endpoint (apiEndpoints.md), keeping the api_key
// server-side. Resolves null for unknown slugs or any failure so the page can
// fall back to static content.
// `type` (projectType.js's CAMPAIGN / PETITION) tells the backend WHICH Gramsci
// collection to look the slug up in — the two live in separate tables with
// separate name lookups, and a slug on its own can't say which it belongs to.
// The caller always knows, because the route it is rendering for already
// decided: /petitions/:slug is a petition, /:slug is a campaign.
//
// It only picks the lookup. Which page renders is still decided by the returned
// record's own project_type, so a wrong or omitted hint costs a miss, never a
// mis-rendered page. Omitted means campaign — what every call meant before
// petitions existed.
export async function fetchGramsciProject(slug, type) {
  try {
    const res = await fetch(`${API_BASE}/api/project`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, type }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// Consent statement ({ hebrew, arabic } or null). `slug` only matters on mock data.
export async function fetchConsentTerms(slug) {
  try {
    const query = slug ? `?slug=${encodeURIComponent(slug)}` : '';
    const res = await fetch(`${API_BASE}/api/consent-terms${query}`);
    if (!res.ok) return null;
    const body = await res.json();
    return (body && body.consent_terms) || null;
  } catch {
    return null;
  }
}

// The visitor's country as an ISO code ('GB'), resolved server-side from their
// IP — the English page preselects a currency from it. Resolves null when the
// country is unknown or the lookup fails; the page then preselects nothing.
export async function fetchGeoCountry() {
  try {
    const res = await fetch(`${API_BASE}/api/geo-country`);
    if (!res.ok) return null;
    const { country } = await res.json();
    return country || null;
  } catch {
    return null;
  }
}

// Send the English page's check/wire/DAF form. The backend forwards it to the
// client's webhooks, which email the donor the instructions. Resolves
// { ok: true } or { ok: false, error } — never throws.
export async function submitOfflineDonation(details) {
  try {
    const res = await fetch(`${API_BASE}/api/offline-donation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(details),
    });
    if (res.ok) return { ok: true };
    const body = await res.json().catch(() => ({}));
    return { ok: false, error: body.error || 'Something went wrong. Please try again.' };
  } catch {
    return { ok: false, error: 'Could not reach the server. Please check your connection and try again.' };
  }
}
